/**
 * @graphql cc
 */
import { graphql } from 'react-relay';
import gql from 'graphql-tag';
import { useCallback, useState, useEffect } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop';
import type { useHomeWidgetStateUserPreferencesFragment$data } from './__generated__/useHomeWidgetStateUserPreferencesFragment.graphql';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import type {
	useHomeWidgetStateHomeWidgetsMutation as useHomeWidgetStateHomeWidgetsMutation$data,
	useHomeWidgetStateHomeWidgetsMutationVariables as useHomeWidgetStateHomeWidgetsMutation$variables,
} from './__types__/useHomeWidgetStateHomeWidgetsMutation';
import { HomeWidgetsQuery } from './HomeWidgetsQuery.graphql';
import type { HomeWidgetsQuery as HomeWidgetsData } from './__types__/HomeWidgetsQuery';
import { HomeWidgetState } from './__types__/HomeWidgetsQuery';

export const useHomeWidgetState = (
	id: string,
	defaultState?: HomeWidgetState,
	initialStateOverride?: HomeWidgetState,
) => {
	const { isLicensed } = useSessionData();
	const {
		data,
		loading,
		error: queryError,
	} = useQuery<HomeWidgetsData>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		HomeWidgetsQuery,
		{
			errorPolicy: 'all',
			// Use cache-first for both licensed and unlicensed users to allow cache reads
			fetchPolicy: 'cache-first',
		},
	);
	const [updateWidgetState, { error: mutationError }] = useMutation<
		useHomeWidgetStateHomeWidgetsMutation$data,
		useHomeWidgetStateHomeWidgetsMutation$variables
	>(
		gql`
			mutation useHomeWidgetStateHomeWidgetsMutation($id: ID!, $state: HomeWidgetState!) {
				updateUserPreferences(userPreferences: { homeWidget: { id: $id, state: $state } }) {
					homeWidgets {
						id
						state
					}
				}
			}
		`,
		{
			ignoreResults: true,
		},
	);
	const [stateOverride, setStateOverride] = useState(initialStateOverride);
	useEffect(() => {
		setStateOverride(initialStateOverride);
	}, [initialStateOverride]);
	// Use the widget state override, falling back to saved prefrence, falling back to defaultState, falling back to collapsed
	const widgetState =
		stateOverride ||
		data?.userPreferences.homeWidgets.find((widget) => widget.id === id)?.state ||
		defaultState ||
		HomeWidgetState.COLLAPSED;
	const writeFragmentInterop = useWriteFragmentInterop<
		Omit<useHomeWidgetStateUserPreferencesFragment$data, ' $fragmentType'>
	>({
		fragmentSpec: graphql`
			fragment useHomeWidgetStateUserPreferencesFragment on UserPreferences {
				homeWidgets {
					id
					state
					__typename
				}
				__typename
			}
		`,
	});
	const setWidgetState = useCallback(
		(newState: HomeWidgetState) => {
			if (isLicensed) {
				void updateWidgetState({
					variables: {
						id,
						state: newState,
					},
				});
			}
			const homeWidgets = (data?.userPreferences.homeWidgets || []).filter(
				(widget) => widget.id !== id,
			);
			homeWidgets.push({
				id,
				state: newState,
				__typename: 'HomeWidget',
			});
			/**
			 * This write into cache serves two purposes:
			 *   1. To initially populate the cache in the case where
			 *      a preference hasn't yet been saved or the user is unlicensed.
			 *   2. To optimistically update the cache, since we're doing #1 anyway
			 */

			writeFragmentInterop({
				fragmentId: '$ROOT_QUERY.userPreferences',
				data: {
					homeWidgets,
					__typename: 'UserPreferences',
				},
			});

			// After the user explicitly changes state, clear the state override
			setStateOverride(undefined);
		},
		[id, data, isLicensed, updateWidgetState, writeFragmentInterop],
	);

	const error = mutationError || queryError;

	return { widgetState, setWidgetState, loading, error };
};
